/**
 * MarkdownToolbar
 *
 * A small selection-aware formatting toolbar that sits above a
 * controlled `<textarea>` (or any MUI `TextField` with `multiline`
 * + `inputRef`). Each button transforms the current selection into
 * the matching Markdown subset that `email_markdown.py` understands.
 *
 * Why we don't use a real WYSIWYG editor:
 *   - Email rendering is server-driven (`/email-preview`), so a
 *     WYSIWYG editor's local rendering would diverge from what the
 *     customer actually receives.
 *   - The Markdown subset is small + auditable. Adding a multi-MB
 *     editor dependency for ~8 buttons is overkill.
 *   - Operators get a faithful, clickable editor without any
 *     drift between preview and reality.
 *
 * Selection semantics:
 *   - If a range is selected, the button wraps THAT range.
 *   - If only the cursor is placed, the button inserts a placeholder
 *     ("text", "Label", etc.) and selects it so the operator can
 *     start typing immediately.
 *   - For block-level transforms (lists, HR), we extend to the start
 *     of the line so `- ` / `1. ` / `---` end up at column 0.
 *
 * Keyboard shortcuts:
 *   - Ctrl/Cmd + B  → bold
 *   - Ctrl/Cmd + I  → italic
 *   - Ctrl/Cmd + K  → link dialog
 *   - Ctrl/Cmd + .  → image dialog (memorable: "img")
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  HorizontalRule as HRuleIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

interface MarkdownToolbarProps {
  /** Ref to the underlying textarea (set via TextField's `inputRef`). */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /** Current value of the textarea (controlled). */
  value: string;
  /** Setter — called with the next value after a transform. */
  onChange: (next: string) => void;
  /** Disable the whole toolbar (e.g. while sending). */
  disabled?: boolean;
}

/**
 * Insert / wrap helper that operates on the textarea's current
 * selection. Centralizes the selection plumbing so each button is
 * a 1-line declaration of "before/after wrapper + placeholder".
 */
function applyToSelection(
  ref: React.RefObject<HTMLTextAreaElement>,
  value: string,
  onChange: (next: string) => void,
  before: string,
  after: string,
  placeholder: string
) {
  const ta = ref.current;
  if (!ta) return;
  const start = ta.selectionStart ?? 0;
  const end = ta.selectionEnd ?? 0;
  const selected = value.slice(start, end);
  const inner = selected || placeholder;
  const next = value.slice(0, start) + before + inner + after + value.slice(end);

  onChange(next);

  // Restore a sensible selection so the operator can keep typing.
  // If text was selected, leave it selected (now wrapped). If only
  // the caret was placed, select the inserted placeholder so they
  // can replace it immediately.
  const cursorStart = start + before.length;
  const cursorEnd = cursorStart + inner.length;
  // setTimeout ensures the controlled re-render has flushed before
  // we move the caret. Without it, React would clobber our
  // selection on the next render.
  setTimeout(() => {
    ta.focus();
    ta.setSelectionRange(cursorStart, cursorEnd);
  }, 0);
}

/**
 * Block-level helper: prepends a marker (`- `, `1. `, `---\n`) to
 * each line in the selection, or to the current line if no selection.
 * Ensures the marker lands at column 0 by extending the selection
 * back to the previous newline first.
 */
function applyToBlock(
  ref: React.RefObject<HTMLTextAreaElement>,
  value: string,
  onChange: (next: string) => void,
  /** Function that, given a 0-based index, returns the marker for that line. */
  marker: (index: number) => string,
  /** Placeholder text for empty lines. */
  placeholder: string
) {
  const ta = ref.current;
  if (!ta) return;
  const start = ta.selectionStart ?? 0;
  const end = ta.selectionEnd ?? 0;
  // Extend `start` back to the start of its line.
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const block = value.slice(lineStart, end);
  const lines = block.length === 0 ? [''] : block.split('\n');
  const transformed = lines
    .map((ln, i) => `${marker(i)}${ln || (i === 0 ? placeholder : '')}`)
    .join('\n');
  const next = value.slice(0, lineStart) + transformed + value.slice(end);
  onChange(next);
  const newEnd = lineStart + transformed.length;
  setTimeout(() => {
    ta.focus();
    ta.setSelectionRange(lineStart, newEnd);
  }, 0);
}

/**
 * Insert a freestanding block (used for HR `---`). Always lands on
 * its own line: prepends a `\n` if we're not already at column 0,
 * always appends a `\n` after.
 */
function insertBlock(
  ref: React.RefObject<HTMLTextAreaElement>,
  value: string,
  onChange: (next: string) => void,
  block: string
) {
  const ta = ref.current;
  if (!ta) return;
  const start = ta.selectionStart ?? 0;
  const atLineStart = start === 0 || value[start - 1] === '\n';
  const insert = (atLineStart ? '' : '\n') + block + '\n';
  const next = value.slice(0, start) + insert + value.slice(start);
  onChange(next);
  const newPos = start + insert.length;
  setTimeout(() => {
    ta.focus();
    ta.setSelectionRange(newPos, newPos);
  }, 0);
}

const MarkdownToolbar: React.FC<MarkdownToolbarProps> = ({
  textareaRef,
  value,
  onChange,
  disabled,
}) => {
  // ---- Link / Image dialogs --------------------------------------
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const [imageOpen, setImageOpen] = useState(false);
  const [imageAlt, setImageAlt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageWidthPx, setImageWidthPx] = useState<string>('600');
  const [imageError, setImageError] = useState<string | null>(null);

  // Track the selection at the moment a dialog opens, so we can
  // insert into the right place even if focus moved to the dialog.
  const savedSelection = useRef<{ start: number; end: number; selected: string } | null>(null);

  const captureSelection = () => {
    const ta = textareaRef.current;
    if (!ta) {
      savedSelection.current = { start: value.length, end: value.length, selected: '' };
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    savedSelection.current = { start, end, selected: value.slice(start, end) };
  };

  // ---- Button handlers -------------------------------------------
  const onBold = useCallback(
    () => applyToSelection(textareaRef, value, onChange, '**', '**', 'bold text'),
    [textareaRef, value, onChange]
  );
  const onItalic = useCallback(
    () => applyToSelection(textareaRef, value, onChange, '*', '*', 'italic text'),
    [textareaRef, value, onChange]
  );
  const onCode = useCallback(
    () => applyToSelection(textareaRef, value, onChange, '`', '`', 'code'),
    [textareaRef, value, onChange]
  );
  const onBullet = useCallback(
    () => applyToBlock(textareaRef, value, onChange, () => '- ', 'list item'),
    [textareaRef, value, onChange]
  );
  const onNumbered = useCallback(
    () => applyToBlock(textareaRef, value, onChange, (i) => `${i + 1}. `, 'list item'),
    [textareaRef, value, onChange]
  );
  const onHR = useCallback(
    () => insertBlock(textareaRef, value, onChange, '---'),
    [textareaRef, value, onChange]
  );

  const openLinkDialog = () => {
    captureSelection();
    setLinkLabel(savedSelection.current?.selected || '');
    setLinkUrl('');
    setLinkOpen(true);
  };
  const submitLinkDialog = () => {
    const sel = savedSelection.current;
    if (!sel) return;
    const label = linkLabel.trim() || 'link';
    const url = linkUrl.trim();
    if (!url) {
      setLinkOpen(false);
      return;
    }
    const insert = `[${label}](${url})`;
    const next = value.slice(0, sel.start) + insert + value.slice(sel.end);
    onChange(next);
    const cursorPos = sel.start + insert.length;
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
    setLinkOpen(false);
  };

  const openImageDialog = () => {
    captureSelection();
    setImageAlt('');
    setImageUrl('');
    setImageWidthPx('600');
    setImageError(null);
    setImageOpen(true);
  };
  const submitImageDialog = () => {
    const sel = savedSelection.current;
    if (!sel) return;
    const url = imageUrl.trim();
    if (!url) {
      setImageError('Image URL is required.');
      return;
    }
    if (!url.toLowerCase().startsWith('https://')) {
      setImageError('Email images must be hosted at an https:// URL.');
      return;
    }
    const widthNum = parseInt(imageWidthPx, 10);
    const widthHint =
      Number.isFinite(widthNum) && widthNum >= 32 && widthNum <= 1200
        ? ` {${widthNum}}`
        : '';
    const insert = `![${imageAlt.trim()}${widthHint}](${url})`;
    const next = value.slice(0, sel.start) + insert + value.slice(sel.end);
    onChange(next);
    const cursorPos = sel.start + insert.length;
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
    setImageOpen(false);
  };

  // ---- Keyboard shortcuts ----------------------------------------
  // Bind on the textarea itself so they don't fire when the operator
  // is typing in another field of the dialog.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const handler = (e: KeyboardEvent) => {
      const cmd = e.ctrlKey || e.metaKey;
      if (!cmd) return;
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        onBold();
      } else if (key === 'i') {
        e.preventDefault();
        onItalic();
      } else if (key === 'k') {
        e.preventDefault();
        openLinkDialog();
      } else if (key === '.') {
        // "img" mnemonic: Ctrl+. opens the image dialog.
        e.preventDefault();
        openImageDialog();
      }
    };
    ta.addEventListener('keydown', handler);
    return () => ta.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textareaRef.current, value]);

  // ---- Render ----------------------------------------------------
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 0.5,
          mb: 0.75,
          bgcolor: 'background.paper',
        }}
      >
        <ToolbarBtn label="Bold (Ctrl/Cmd+B)" onClick={onBold} disabled={disabled}>
          <BoldIcon fontSize="small" />
        </ToolbarBtn>
        <ToolbarBtn label="Italic (Ctrl/Cmd+I)" onClick={onItalic} disabled={disabled}>
          <ItalicIcon fontSize="small" />
        </ToolbarBtn>
        <ToolbarBtn label="Inline code" onClick={onCode} disabled={disabled}>
          <CodeIcon fontSize="small" />
        </ToolbarBtn>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ToolbarBtn label="Bullet list" onClick={onBullet} disabled={disabled}>
          <BulletIcon fontSize="small" />
        </ToolbarBtn>
        <ToolbarBtn label="Numbered list" onClick={onNumbered} disabled={disabled}>
          <NumberedIcon fontSize="small" />
        </ToolbarBtn>
        <ToolbarBtn label="Horizontal rule" onClick={onHR} disabled={disabled}>
          <HRuleIcon fontSize="small" />
        </ToolbarBtn>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ToolbarBtn label="Insert link (Ctrl/Cmd+K)" onClick={openLinkDialog} disabled={disabled}>
          <LinkIcon fontSize="small" />
        </ToolbarBtn>
        <ToolbarBtn label="Insert image (Ctrl/Cmd+.)" onClick={openImageDialog} disabled={disabled}>
          <ImageIcon fontSize="small" />
        </ToolbarBtn>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                Markdown supported:
              </Typography>
              <Typography variant="caption" component="div">**bold**, *italic*, `code`</Typography>
              <Typography variant="caption" component="div">- bullet list / 1. numbered list</Typography>
              <Typography variant="caption" component="div">[link](https://...)</Typography>
              <Typography variant="caption" component="div">![alt {320}](https://...)</Typography>
              <Typography variant="caption" component="div">--- horizontal rule</Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Image URLs must be https://. Width hint in {`{px}`} (32–1200, default 600).
              </Typography>
            </Box>
          }
        >
          <IconButton size="small" disabled={disabled}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ---- Link dialog ---- */}
      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Label (visible text)"
              size="small"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="URL"
              size="small"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              fullWidth
              placeholder="https://..."
              helperText="Must be an https://, http://, or mailto: URL."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button
            onClick={submitLinkDialog}
            variant="contained"
            disabled={!linkUrl.trim()}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Image dialog ---- */}
      <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert image</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Image URL (https only)"
              size="small"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              fullWidth
              autoFocus
              placeholder="https://cdn.example.com/banner.png"
              error={!!imageError}
              helperText={imageError || 'Must be an absolute https:// URL. Host the image on your CDN or the Passio assets bucket.'}
            />
            <TextField
              label="Alt text"
              size="small"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              fullWidth
              helperText="Read by screen readers + shown when the recipient blocks remote images."
            />
            <TextField
              label="Display width (px)"
              size="small"
              value={imageWidthPx}
              onChange={(e) => setImageWidthPx(e.target.value.replace(/[^\d]/g, ''))}
              fullWidth
              helperText="32–1200. Default 600 (full container width). Image is responsive — this is the desktop max."
            />
            {imageUrl.trim().toLowerCase().startsWith('https://') && (
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Preview:
                </Typography>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                  }}
                >
                  <img
                    src={imageUrl.trim()}
                    alt={imageAlt}
                    style={{ maxWidth: '100%', maxHeight: 240, height: 'auto' }}
                    onError={() =>
                      setImageError('Image failed to load — check the URL is publicly reachable.')
                    }
                    onLoad={() => setImageError(null)}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageOpen(false)}>Cancel</Button>
          <Button
            onClick={submitImageDialog}
            variant="contained"
            disabled={!imageUrl.trim()}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Small helper button so the toolbar JSX above isn't full of repeated
// Tooltip + IconButton boilerplate.
const ToolbarBtn: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, disabled, children }) => (
  <Tooltip title={label}>
    <span>
      <IconButton size="small" onClick={onClick} disabled={disabled}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export default MarkdownToolbar;
